import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { trackEvent } from "@/hooks/useAnalytics";
import { ShieldCheck, Zap, Award, Bot, BookOpen, Check, ArrowRight, Lock, Gift, Ticket } from "lucide-react";
import { toast } from "sonner";

const FEATURES = [
  { icon: BookOpen, t: "140 hours of written curriculum across 15 modules (L1 · L2 · L3 · S-98)" },
  { icon: ShieldCheck, t: "NICET Level 1 Foundation exam preparation" },
  { icon: Zap, t: "NYC S-98 Certificate of Fitness review module" },
  { icon: Bot, t: "Unlimited AI Tutor Q&A (Claude Sonnet 4.5)" },
  { icon: Award, t: "50-question final practice exam + QR-verified certificate" },
];

// Capture marketing UTMs from the URL into sessionStorage so they survive
// page-to-page navigation up until the user actually checks out. Last-touch model.
const ATTRIBUTION_STORAGE_KEY = "apm_attribution_v1";

function captureAttributionFromUrl(searchParams) {
  const keys = ["utm_source", "utm_campaign", "utm_content", "utm_medium", "utm_term"];
  const captured = {};
  let anyPresent = false;
  for (const k of keys) {
    const v = searchParams.get(k);
    if (v) { captured[k] = v.slice(0, 80); anyPresent = true; }
  }
  if (!anyPresent) return;
  captured.landed_at = new Date().toISOString();
  try {
    sessionStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(captured));
  } catch { /* private mode / quota — non-fatal */ }
}

function readStoredAttribution() {
  try {
    const raw = sessionStorage.getItem(ATTRIBUTION_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export default function Enroll() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState(params.get("tab") === "gift" ? "gift" : "self");

  // Gift form state
  const [gift, setGift] = useState({ recipient_email: "", recipient_name: "", personal_note: "" });
  const [giftLoading, setGiftLoading] = useState(false);

  // Capture UTMs on mount — they may be on this URL right now if the user
  // clicked a drip-email link. Persist for later checkout submission.
  useEffect(() => { captureAttributionFromUrl(params); }, [params]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/payments/packages");
        setPkg(data.packages[0]);
      } catch { /* package fetch fails fall through to default $149 */ }
    })();
  }, []);

  useEffect(() => {
    if (user?.paid && tab === "self") nav("/dashboard", { replace: true });
  }, [user, nav, tab]);

  const checkout = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: "full",
        origin_url: window.location.origin,
        attribution: readStoredAttribution(),
      });
      trackEvent("begin_checkout", { value: 149, currency: "USD", items: [{ item_id: "full", item_name: "APM Fire Alarm Foundation", price: 149, quantity: 1 }] });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start checkout");
      setLoading(false);
    }
  };

  const giftCheckout = async () => {
    if (!gift.recipient_email.trim()) {
      toast.error("Recipient email is required");
      return;
    }
    setGiftLoading(true);
    try {
      const { data } = await api.post("/payments/gift/checkout", {
        recipient_email: gift.recipient_email.trim(),
        recipient_name: gift.recipient_name.trim() || null,
        personal_note: gift.personal_note.trim() || null,
        origin_url: window.location.origin,
      });
      trackEvent("begin_checkout", { value: 149, currency: "USD", items: [{ item_id: "gift", item_name: "APM Fire Alarm Gift", price: 149, quantity: 1 }] });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start gift checkout");
      setGiftLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 py-14" data-testid="enroll-page">
        <div className="overline mb-3"><span className="red-bar" />Enrollment</div>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter mb-4">
          Unlock the full course.
        </h1>
        <p className="text-zinc-700 max-w-2xl leading-relaxed mb-8">
          One-time enrollment unlocks every lesson, quiz, the NYC S-98 review, the 50-question
          final practice exam, your AI Tutor, and a QR-verified certificate.
        </p>

        {/* Tabs */}
        <div className="flex gap-1 mb-8 border-b-2 border-zinc-200">
          <button
            onClick={() => setTab("self")}
            className={`px-5 py-3 text-sm font-mono uppercase tracking-wider transition-colors -mb-0.5 border-b-2 ${
              tab === "self" ? "border-red-600 text-red-600" : "border-transparent text-zinc-500 hover:text-zinc-800"
            }`}
            data-testid="enroll-tab-self"
          >
            Enroll for myself
          </button>
          <button
            onClick={() => setTab("gift")}
            className={`px-5 py-3 text-sm font-mono uppercase tracking-wider transition-colors -mb-0.5 border-b-2 inline-flex items-center gap-2 ${
              tab === "gift" ? "border-red-600 text-red-600" : "border-transparent text-zinc-500 hover:text-zinc-800"
            }`}
            data-testid="enroll-tab-gift"
          >
            <Gift className="w-4 h-4" /> Gift this course
          </button>
          <Link
            to="/redeem"
            className="ml-auto px-5 py-3 text-sm font-mono uppercase tracking-wider text-zinc-600 hover:text-red-600 inline-flex items-center gap-2"
            data-testid="enroll-redeem-link"
          >
            <Ticket className="w-4 h-4" /> Have a gift code?
          </Link>
        </div>

        {tab === "self" && (
          <div className="grid lg:grid-cols-5 gap-6" data-testid="enroll-self-panel">
            <div className="lg:col-span-3 card-tech p-8" data-testid="enroll-features-card">
              <div className="overline mb-4">What's included</div>
              <ul className="space-y-3">
                {FEATURES.map((f, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-7 h-7 bg-red-50 text-red-600 grid place-items-center flex-shrink-0">
                      <f.icon className="w-3.5 h-3.5" strokeWidth={2.5} />
                    </div>
                    <div className="text-sm text-zinc-800 leading-relaxed">{f.t}</div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:col-span-2 border-4 border-red-600 p-8 flex flex-col" data-testid="enroll-price-card">
              <div className="overline">APM Full Enrollment</div>
              <div className="font-heading text-6xl font-bold tracking-tighter mt-3">
                ${pkg?.amount ?? 149}
                <span className="text-lg font-medium text-zinc-500 ml-2">one-time</span>
              </div>
              <p className="text-xs text-zinc-600 mt-3 leading-relaxed">
                Lifetime access + all updates. No subscription. Secure checkout via Stripe.
              </p>
              <button
                onClick={checkout}
                disabled={loading}
                className="btn-primary justify-center mt-6"
                data-testid="enroll-checkout-btn"
              >
                {loading ? "Redirecting…" : (<>Enroll now <ArrowRight className="w-4 h-4" /></>)}
              </button>
              <div className="flex items-center gap-2 text-xs text-zinc-500 mt-4 font-mono">
                <Lock className="w-3 h-3" /> Secure payment · Powered by Stripe
              </div>
              <div className="mt-auto pt-6 text-xs text-zinc-500 border-t border-zinc-100">
                Signed in as <span className="font-semibold text-zinc-700">{user?.email}</span>
              </div>
            </div>
          </div>
        )}

        {tab === "gift" && (
          <div className="grid lg:grid-cols-5 gap-6" data-testid="enroll-gift-panel">
            <div className="lg:col-span-3 card-tech p-8">
              <div className="flex items-center gap-2 mb-4">
                <Gift className="w-5 h-5 text-red-600" />
                <div className="overline"><span className="red-bar" />Gift this course</div>
              </div>
              <p className="text-zinc-700 leading-relaxed mb-6">
                Pay $149, we email them a personal gift code. They redeem it with a click — the full
                140-hour curriculum unlocks on their account. Works for classmates, coworkers,
                apprentices, family, or yourself-at-a-different-email.
              </p>

              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-mono uppercase tracking-widest text-zinc-600 mb-1">
                    Recipient email <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="email"
                    value={gift.recipient_email}
                    onChange={(e) => setGift({ ...gift, recipient_email: e.target.value })}
                    placeholder="friend@example.com"
                    className="w-full border-2 border-zinc-200 focus:border-red-600 outline-none px-3 py-2.5 font-mono text-sm"
                    data-testid="enroll-gift-email"
                  />
                </div>
                <div>
                  <label className="block text-xs font-mono uppercase tracking-widest text-zinc-600 mb-1">
                    Recipient name <span className="text-zinc-400">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={gift.recipient_name}
                    onChange={(e) => setGift({ ...gift, recipient_name: e.target.value })}
                    placeholder="Taylor"
                    className="w-full border-2 border-zinc-200 focus:border-red-600 outline-none px-3 py-2.5 text-sm"
                    data-testid="enroll-gift-name"
                  />
                </div>
                <div>
                  <label className="block text-xs font-mono uppercase tracking-widest text-zinc-600 mb-1">
                    Personal note <span className="text-zinc-400">(optional, up to 300 chars)</span>
                  </label>
                  <textarea
                    value={gift.personal_note}
                    onChange={(e) => setGift({ ...gift, personal_note: e.target.value.slice(0, 300) })}
                    placeholder="Hey — thought this would help with your NICET prep. Good luck!"
                    rows={4}
                    className="w-full border-2 border-zinc-200 focus:border-red-600 outline-none px-3 py-2.5 text-sm resize-none"
                    data-testid="enroll-gift-note"
                  />
                  <div className="text-right text-[11px] text-zinc-400 font-mono mt-1">
                    {gift.personal_note.length}/300
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-2 border-4 border-red-600 p-8 flex flex-col">
              <div className="overline">Gift purchase</div>
              <div className="font-heading text-6xl font-bold tracking-tighter mt-3">
                ${pkg?.amount ?? 149}
                <span className="text-lg font-medium text-zinc-500 ml-2">one-time</span>
              </div>
              <p className="text-xs text-zinc-600 mt-3 leading-relaxed">
                Your card is charged once. They get a redemption email within seconds of payment. You'll be
                notified the moment they redeem.
              </p>
              <button
                onClick={giftCheckout}
                disabled={giftLoading || !gift.recipient_email.trim()}
                className="btn-primary justify-center mt-6 disabled:opacity-50"
                data-testid="enroll-gift-checkout-btn"
              >
                {giftLoading ? "Redirecting…" : (<><Gift className="w-4 h-4" /> Send gift</>)}
              </button>
              <div className="flex items-center gap-2 text-xs text-zinc-500 mt-4 font-mono">
                <Lock className="w-3 h-3" /> Secure payment · Powered by Stripe
              </div>
              <ul className="mt-6 pt-4 border-t border-zinc-100 space-y-2 text-xs text-zinc-600">
                <li className="flex items-start gap-2"><Check className="w-3 h-3 text-green-600 mt-0.5 flex-shrink-0" /> Recipient gets a branded redemption email</li>
                <li className="flex items-start gap-2"><Check className="w-3 h-3 text-green-600 mt-0.5 flex-shrink-0" /> You get a confirmation + copy of the code</li>
                <li className="flex items-start gap-2"><Check className="w-3 h-3 text-green-600 mt-0.5 flex-shrink-0" /> Code expires only on redemption (no deadline)</li>
              </ul>
            </div>
          </div>
        )}

        <div className="mt-8 text-sm text-zinc-600">
          <Link to="/dashboard" className="hover:text-red-600" data-testid="enroll-back-link">
            ← Back to dashboard
          </Link>
        </div>
      </main>
    </div>
  );
}
